/**
 * user-frontend/src/app.js
 *
 * FIXES:
 *  - BUG-007: ProtectedRoute usava `loading` (undefined) do AuthContext
 *    → agora usa isLoading correctamente
 *  - Provider wrap correcto: AuthProvider fora de Provider Redux
 *  - Removido AdminDashboard das rotas do user-frontend (é app separada)
 *  - Adicionado ErrorBoundary para evitar crash total da app
 *  - RequestsPage importada correctamente (estava a apontar para versão errada)
 */

import React, { useContext, Suspense, lazy } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AuthContext, AuthProvider } from './context/AuthContext.js';
import { destinoAposAuth } from './utils/destinoAuth';
import { ModoRecrutamentoProvider, useModoRecrutamento } from './hooks/useModoRecrutamento';
import { Navbar } from './components/Navbar.js';
import { Sidebar } from './components/sidebar.js';
import PainelRecrutamento from './components/PainelRecrutamento';
import store from './redux/store';

// Lazy load para code splitting
const Login           = lazy(() => import('./pages/Login.js'));
const Register        = lazy(() => import('./pages/Register.js'));
const LandingPage     = lazy(() => import('./pages/landingPage.js'));
const DozyEmpresas    = lazy(() => import('./pages/DozyEmpresas.js'));
const UserDashboard   = lazy(() => import('./pages/UserDashboard.js'));
const WorkerDashboard = lazy(() => import('./pages/WorkerDashboard.js'));
const WorkerProfile   = lazy(() => import('./pages/WorkerProfile.js'));
const Obras           = lazy(() => import('./pages/Obras.js'));
const Convites        = lazy(() => import('./pages/Convites.js'));
const ObraNova        = lazy(() => import('./pages/ObraNova.js'));
const ObraDetalhe     = lazy(() => import('./pages/ObraDetalhe.js'));
// Área empresarial. Cada secção carrega por si: quem entra para decidir uma
// aprovação não tem de descarregar a gestão de pessoas e de planos com ela.
const EmpresaArea     = lazy(() => import('./pages/empresa/EmpresaArea.js'));
const EmpresaDinheiro = lazy(() => import('./pages/empresa/EmpresaDinheiro.js'));
const EmpresaPainel   = lazy(() => import('./pages/empresa/EmpresaPainel.js'));
const EmpresaObras    = lazy(() => import('./pages/empresa/EmpresaObras.js'));
const EmpresaCustos   = lazy(() => import('./pages/empresa/EmpresaCustos.js'));
const EmpresaPessoas  = lazy(() => import('./pages/empresa/EmpresaPessoas.js'));
const EmpresaMais     = lazy(() => import('./pages/empresa/EmpresaMais.js'));
const EmpresaNova     = lazy(() => import('./pages/empresa/EmpresaNova.js'));
const EmpresaServicos = lazy(() => import('./pages/empresa/EmpresaServicos.js'));
const EmpresaPrestadora = lazy(() => import('./pages/empresa/EmpresaPrestadora.js'));
const EmpresaOportunidades = lazy(() => import('./pages/empresa/EmpresaOportunidades.js'));
const EmpresaPerfil   = lazy(() => import('./pages/EmpresaPerfil.js'));
const ConfirmarTelefone = lazy(() => import('./pages/ConfirmarTelefone.js'));
const Premium         = lazy(() => import('./pages/Premium.js'));
const MinhaCasa       = lazy(() => import('./pages/MinhaCasa.js'));
const NearbyWorkers   = lazy(() => import('./pages/NearbyWorkers.js'));
const Disputes        = lazy(() => import('./pages/Disputes.js'));
const Appointments    = lazy(() => import('./pages/Appointments.js'));
const Notifications   = lazy(() => import('./pages/Notifications.js'));
const WorkerVerification = lazy(() => import('./pages/WorkerVerification.js'));
const ForgotPassword  = lazy(() => import('./pages/ForgotPassword.js'));
const ResetPassword   = lazy(() => import('./pages/ResetPassword.js'));
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy.js'));
const Terms           = lazy(() => import('./pages/Terms.js'));

const PageLoader = () => (
  <div className="page-loader">
    <div className="loader-ring"><div/><div/><div/><div/></div>
    <p className="loader-text">A carregar...</p>
  </div>
);

// FIX: usa isLoading (existia como "loading" mas não era exposto pelo AuthContext)
const ProtectedRoute = ({ element, requiredRole }) => {
  const { user, isLoading } = useContext(AuthContext);
  const location = useLocation();

  if (isLoading) return <PageLoader />;
  if (!user) {
    // Só se guarda o destino quando a lista fechada o aceita. O resto
    // continua a ir para /login sem query — não é um redireccionamento aberto.
    const pedido = `${location.pathname}${location.search || ''}`;
    const destino = destinoAposAuth(`?destino=${encodeURIComponent(pedido)}`);
    const para = destino ? `/login?destino=${encodeURIComponent(destino)}` : '/login';
    return <Navigate to={para} replace />;
  }
  if (requiredRole && user.role !== requiredRole) {
    // FIX: role desconhecido/indefinido ia para /worker e criava um
    // loop de redirects (página em branco). Agora só redireciona para
    // rotas de roles conhecidos; caso contrário volta ao login.
    if (user.role === 'client') return <Navigate to="/dashboard" replace />;
    if (user.role === 'worker') return <Navigate to="/worker" replace />;
    return <Navigate to="/login" replace />;
  }
  return element;
};

// Enquanto se recruta, estas rotas não têm mercado para mostrar. O
// profissional volta ao painel dele; o cliente vê o aviso, com a conta intacta.
const MercadoFechado = ({ element }) => {
  const { activo } = useModoRecrutamento();
  const { user } = useContext(AuthContext);
  if (!activo) return element;
  if (user?.role === 'worker') return <Navigate to="/worker" replace />;
  return (
    <div className="has-bottom-nav dozy-app-shell" style={{ minHeight: '100vh' }}>
      <Navbar />
      <div className="dashboard-grid">
        <Sidebar />
        <main className="main-panel">
          <PainelRecrutamento />
        </main>
      </div>
    </div>
  );
};

const App = () => (
  <Provider store={store}>
    {/* AuthProvider fora do Router — acesso ao contexto em qualquer ponto */}
    <Router>
      <ModoRecrutamentoProvider>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/"          element={<LandingPage />} />
          {/* Porta pública das empresas. Fora do painel do cliente. */}
          <Route path="/empresas" element={<DozyEmpresas />} />
          <Route path="/login"     element={<Login />} />
          <Route path="/register"  element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password"  element={<ResetPassword />} />
          <Route path="/reset"           element={<ResetPassword />} />
          <Route path="/privacidade" element={<PrivacyPolicy />} />
          <Route path="/termos"      element={<Terms />} />

          <Route path="/dashboard"
            element={<ProtectedRoute element={<UserDashboard />}   requiredRole="client" />} />
          <Route path="/worker"
            element={<ProtectedRoute element={<WorkerDashboard />} requiredRole="worker" />} />
          <Route path="/worker-verification"
            element={<ProtectedRoute element={<WorkerVerification />} requiredRole="worker" />} />
          <Route path="/worker-profile/:workerId"
            element={<ProtectedRoute element={<WorkerProfile />} />} />
          {/* Obras & Projetos — trabalhos maiores, com avaliação técnica e
              propostas comparáveis. */}
          <Route path="/obras"      element={<ProtectedRoute element={<MercadoFechado element={<Obras />} />} />} />
          <Route path="/obras/nova" element={<ProtectedRoute element={<MercadoFechado element={<ObraNova />} />} />} />
          <Route path="/obras/:id"  element={<ProtectedRoute element={<MercadoFechado element={<ObraDetalhe />} />} />} />
          {/* Convites a concurso — o lado do prestador do motor de concursos.
              Sem papel exigido: quem responde por uma empresa prestadora é, na
              maioria dos casos, um `client`. Exigir `worker` fechava a porta
              exactamente a quem o Dozy Business diz que se candidata. Quem não
              tem convites vê a lista vazia, que é a resposta certa. */}
          <Route path="/convites"
            element={<ProtectedRoute element={<Convites />} />} />
          {/* Fora da área da empresa: quem abre isto é um cliente a avaliar
              uma proposta, não alguém a gerir a sua própria conta. */}
          <Route path="/empresa-perfil/:businessId"
            element={<ProtectedRoute element={<MercadoFechado element={<EmpresaPerfil />} />} />} />
          <Route path="/empresa" element={<ProtectedRoute element={<EmpresaArea />} />}>
            <Route index            element={<EmpresaPainel />} />
            <Route path="servicos"  element={<EmpresaServicos />} />
            <Route path="prestar"   element={<EmpresaPrestadora />} />
            <Route path="oportunidades" element={<EmpresaOportunidades />} />
            <Route path="obras"     element={<EmpresaObras />} />
            <Route path="custos"    element={<EmpresaCustos />} />
            <Route path="dinheiro"  element={<EmpresaDinheiro />} />
            <Route path="pessoas"   element={<EmpresaPessoas />} />
            <Route path="mais"      element={<EmpresaMais />} />
            <Route path="nova"      element={<EmpresaNova />} />
          </Route>
          <Route path="/confirmar-telefone" element={<ProtectedRoute element={<ConfirmarTelefone />} />} />
          <Route path="/premium"    element={<ProtectedRoute element={<MercadoFechado element={<Premium />} />} />} />
          <Route path="/minha-casa" element={<ProtectedRoute element={<MercadoFechado element={<MinhaCasa />} />} />} />
          <Route path="/nearby-workers"
            element={<ProtectedRoute element={<MercadoFechado element={<NearbyWorkers />} />} requiredRole="client" />} />
          <Route path="/disputes"
            element={<ProtectedRoute element={<MercadoFechado element={<Disputes />} />} />} />
          <Route path="/appointments"
            element={<ProtectedRoute element={<MercadoFechado element={<Appointments />} />} requiredRole="client" />} />
          <Route path="/notifications"
            element={<ProtectedRoute element={<Notifications />} />} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
      </ModoRecrutamentoProvider>
    </Router>
  </Provider>
);

// Wrap com AuthProvider no index.js (já estava correcto)
export default App;
